import api, { orgScoped } from '../lib/api';
import type { Ticket, TicketComment, TicketCounts, TicketListParams, CreateTicketData, UpdateTicketData, TicketUserBrief, TicketType } from '../types/ticket';

type Pagination = { page: number; limit: number; total: number; pages: number };

/**
 * /api/tickets. Every call takes a trailing `scopeOrgId`: under /all the merged page has no
 * ambient organization, so a write is pinned to the ticket's org with the X-Organization-Id
 * header (see orgScoped). Inside an org mount the header is already set and callers pass nothing.
 */
const ticketService = {
  getTickets: (params: TicketListParams = {}, scopeOrgId?: number | null) =>
    api.get<{ success: boolean; data: { tickets: Ticket[]; pagination: Pagination } }>('/tickets', { params, ...orgScoped(scopeOrgId) }),

  getTicket: (id: number, scopeOrgId?: number | null) =>
    api.get<{ success: boolean; data: { ticket: Ticket } }>(`/tickets/${id}`, orgScoped(scopeOrgId)),

  /** A client's request: type, title, message. Organization and company come from the login on the server. */
  createClientRequest: (data: { type: TicketType; title: string; description?: string | null }) =>
    api.post<{ success: boolean; data: { ticket: Ticket } }>('/tickets', data),

  createTicket: (data: CreateTicketData, scopeOrgId?: number | null) =>
    api.post<{ success: boolean; data: { ticket: Ticket } }>('/tickets', data, orgScoped(scopeOrgId)),

  updateTicket: (id: number, data: UpdateTicketData, scopeOrgId?: number | null) =>
    api.patch<{ success: boolean; data: { ticket: Ticket } }>(`/tickets/${id}`, data, orgScoped(scopeOrgId)),

  addComment: (id: number, comment: string, mentions: number[] = [], scopeOrgId?: number | null, clientVisible = false) =>
    api.post<{ success: boolean; data: { comment: TicketComment } }>(`/tickets/${id}/comments`, { comment, mentions, client_visible: clientVisible }, orgScoped(scopeOrgId)),

  /** Models of one organization, for the picker. */
  searchModels: (organizationId: number, search: string) =>
    api.get<{ success: boolean; data: { models: Array<{ id: number; first_name?: string | null; last_name?: string | null; model_name?: string | null; media_uploads?: Array<{ file_path: string }> }> } }>('/organizations/models', { params: { search, limit: 12, page: 1 }, ...orgScoped(organizationId) }),

  deleteComment: (id: number, commentId: number, scopeOrgId?: number | null) =>
    api.delete<{ success: boolean }>(`/tickets/${id}/comments/${commentId}`, orgScoped(scopeOrgId)),

  getCounts: (scopeOrgId?: number | null) =>
    api.get<{ success: boolean; data: TicketCounts }>('/tickets/counts', orgScoped(scopeOrgId)),

  /** Staff who can own a ticket of ONE organization — requires a single org in scope. */
  getAssignees: (organizationId: number) =>
    api.get<{ success: boolean; data: { assignees: Array<TicketUserBrief & { role: string }> } }>('/tickets/assignees', { params: { organization_id: organizationId }, ...orgScoped(organizationId) }),
};

export default ticketService;
